import { Client, Room } from "colyseus.js";
import type { RoomStateLike } from "./types";
import { loadConfig } from "./config";
import { getStoredToken } from "./auth";

export type GameRoom = Room<RoomStateLike>;

export interface NetworkConfig {
  endpoint?: string;
  retryDelayMs?: number;
  onStatus?: (status: string) => void;
}

/**
 * Build a Colyseus client. If `endpoint` is omitted, loads runtime config
 * (Pages /vibe-config.json in prod, localhost in dev).
 *
 * Most callers should `await loadConfig()` themselves at bootstrap and then
 * pass `getConfig().serverUrl` here — this overload exists for tests and for
 * the one-off `createClient()` use case.
 */
export async function createClient(endpoint?: string): Promise<Client> {
  if (endpoint) return new Client(endpoint);
  const cfg = await loadConfig();
  return new Client(cfg.serverUrl);
}

/**
 * Joins a room by name (Colyseus room handler, defaults to "vibe") with a
 * target slug. The slug becomes the per-room source dir (rooms/<slug>/...)
 * and routes via the engine's room registry — see SPEC §wave-3G. Multiple
 * Colyseus instances can share a slug; joinOrCreate matches by `roomSlug`
 * filter so we land on the same logical room across reloads.
 *
 * Retries forever; never throws. Status updates via onStatus.
 *
 * `authToken` is mandatory in prod (engine's onAuth gate rejects joins
 * without one). The default is `getStoredToken()` so callers that already
 * ran `requestPasswordIfNeeded` don't have to thread it explicitly.
 */
export async function joinRoom(
  client: Client,
  slug: string,
  options: {
    handlerName?: string;
    retryDelayMs?: number;
    onStatus?: (s: string) => void;
    authToken?: string;
  } = {},
): Promise<GameRoom> {
  const handlerName = options.handlerName ?? "vibe";
  const retryDelayMs = options.retryDelayMs ?? 2000;
  const onStatus = options.onStatus ?? (() => {});
  const authToken = options.authToken ?? getStoredToken() ?? undefined;

  let attempt = 0;
  while (true) {
    attempt++;
    try {
      onStatus(attempt === 1 ? "connecting..." : `retry #${attempt}...`);
      // Pass roomSlug AND roomId so legacy filters still match — server
      // prefers roomSlug. joinOrCreate filters by these so two clients on
      // the same slug share a room instance (1:1 by default — Colyseus may
      // spin up new ones only when capacity exceeded).
      const joinOptions: Record<string, unknown> = {
        roomSlug: slug,
        roomId: slug,
      };
      if (authToken) joinOptions.authToken = authToken;
      const room = await client.joinOrCreate<RoomStateLike>(handlerName, joinOptions);
      onStatus(`connected to ${slug} (${room.sessionId})`);
      return room;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      console.warn(`[network] join failed (attempt ${attempt}): ${msg}`);
      onStatus(`offline — retrying in ${Math.round(retryDelayMs / 1000)}s (${msg})`);
      await sleep(retryDelayMs);
    }
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

export { Client, Room };
